

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { SplitAuthLayout } from '@/components/auth/SplitAuthLayout';
import { ReCaptcha } from '@/components/forms/renderer/ReCaptcha';
import { AppLoader } from '@/lib/router/components';
import { createLogger } from '@/lib/logger';

const logger = createLogger('pages/register/index');

// Import shadcn components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GoogleIcon } from '@/components/ui/google-icon';

import { authApi } from '@/lib/api/auth.api';
import { useAuth } from '@/components/auth/AuthProvider';
import { useLoginSession } from '@/store/login-session.store';
import { isMobileAuthFlow } from '@/utils/mobile-auth';
import { isTauri, desktopAuthFlow, inAppOAuthFlow } from '@/lib/utils/tauri';
import type { User } from '@scrambl/shared';
interface FormData {
  email: string;
}

interface FormError {
  email?: string;
  general?: string;
}

function isUserData(data: unknown): data is User {
  return Boolean(data && typeof data === 'object' && 'id' in data && 'email' in data);
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken') || searchParams.get('token');
  // Where to send the user after their account is confirmed. Used by the
  // client-portal invite flow to round-trip back to the invite page so the
  // portal token gets redeemed once the user is authenticated.
  const returnUrl = searchParams.get('returnUrl') || searchParams.get('redirect');
  const isAddAccountFlow = searchParams.has('addAccount');
  const { isAuthenticated, isLoading: authLoading, checkUserAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [errors, setErrors] = useState<FormError>({});
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: '',
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const requireCaptcha = Boolean(captchaSiteKey) || !import.meta.env.DEV;
  
  // Add refs to track if we've already applied the email
  const emailFromUrlApplied = useRef(false);

  // Get the setRegistrationSession action from our store
  const setRegistrationSession = useLoginSession(state => state.setRegistrationSession);
  const isConfirmationFlow = useLoginSession(state => state.isConfirmationFlow);
  const clearSession = useLoginSession(state => state.clearSession);

  // Pre-fill email from URL parameters
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam && !emailFromUrlApplied.current) {
      emailFromUrlApplied.current = true;
      setFormData(prev => ({
        ...prev,
        email: normalizeEmail(emailParam)
      }));
    }
  }, [searchParams]); // Empty dependency array to run only once on mount

  useEffect(() => {
    // Redirect authenticated users
    if (isAuthenticated && !authLoading && !isAddAccountFlow) {
      // If there's an invite token, redirect to organizations page
      if (inviteToken) {
        navigate('/organizations', { replace: true });
      } else {
        // Default redirect
        navigate('/today', { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, isAddAccountFlow, navigate, inviteToken]);

  // Fetch invite information if token is present
  useEffect(() => {
    if (inviteToken) {
      // Store the invite token for later use during registration
      localStorage.setItem('pendingInviteToken', inviteToken);
      
      // Set validating state to true
      setIsValidatingToken(true);
      
      // After a short delay, set validating to false since we don't have
      // the functionality to validate the token in this simplified version
      setTimeout(() => {
        setIsValidatingToken(false);
      }, 500);
    }
  }, [inviteToken]);

  // Check form validity whenever form data changes
  useEffect(() => {
    const { email } = formData;
    const isValid = 
      email.trim() !== '' && 
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
      (!requireCaptcha || Boolean(captchaToken));
    
    setIsFormValid(isValid);
  }, [captchaToken, formData, requireCaptcha]);

  // Clear session data when the component mounts
  useEffect(() => {
    // Only clear if we're not in a confirmation flow
    if (!isConfirmationFlow) {
      clearSession();
    }
  }, [clearSession, isConfirmationFlow]);

  // Add an effect to check if we're in a confirmation flow and redirect if needed
  useEffect(() => {
    if (isConfirmationFlow) {
      const confirmationParams = new URLSearchParams();
      if (isAddAccountFlow) {
        confirmationParams.set('addAccount', 'true');
      }
      const confirmationUrl = confirmationParams.toString()
        ? `/register/confirmation?${confirmationParams.toString()}`
        : '/register/confirmation';
      navigate(confirmationUrl);
    }
  }, [isAddAccountFlow, isConfirmationFlow, navigate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nextValue = name === 'email' ? normalizeEmail(value) : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormError]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    // Hide login prompt when user starts typing
    if (showLoginPrompt) {
      setShowLoginPrompt(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormError = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);

      // Pass 'register' as the flow type to indicate this is for account creation
      // For desktop/mobile flows, pass platform so callback handling can return tokens correctly.
      const platform = isTauri()
        ? 'desktop'
        : (isMobileAuthFlow() ? 'mobile' : undefined);
      const response = await authApi.getGoogleLoginUrl(inviteToken || undefined, 'register', platform);

      if (response.success && response.data && 'authUrl' in response.data) {
        const authUrl = response.data.authUrl as string;

        // Desktop flow: use in-app OAuth window for local dev, external browser otherwise
        if (isTauri()) {
          try {
            if (import.meta.env.DEV) {
              // Local dev: use in-app OAuth window.
              const { code, state } = await inAppOAuthFlow(authUrl, '/auth/callback', 300000);
              await authApi.handleGoogleCallback(code, state || '', 'desktop');
            } else {
              // Production: use external browser + deep link flow
              await desktopAuthFlow(authUrl, 300000, { forceNewAccount: isAddAccountFlow });
            }
            await checkUserAuth({ force: true });

            if (inviteToken) {
              navigate('/organizations', { replace: true });
            } else {
              navigate('/today', { replace: true });
            }
          } catch (error) {
            logger.error('[Register] Desktop auth flow failed:', error);
            toast.error(error instanceof Error ? error.message : 'Registration failed');
            setIsGoogleLoading(false);
          }
          return;
        }

        // Web flow: redirect to OAuth URL
        setTimeout(() => {
          window.location.href = authUrl;
        }, 100);
      } else {
        toast.error('Failed to initiate Google sign-in');
        setIsGoogleLoading(false);
      }
    } catch (error) {
      logger.error('[Register] Google sign-in failed:', error);
      toast.error('Failed to connect with Google');
      setIsGoogleLoading(false);
    }
    // Note: Don't set isLoading to false in finally block since we're redirecting
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      setErrors({}); // Clear any existing errors
      setShowLoginPrompt(false); // Reset login prompt
      const normalizedEmail = normalizeEmail(formData.email);

      if (requireCaptcha && !captchaToken) {
        setErrors({
          general: captchaSiteKey
            ? 'Verification is still loading. Please try again in a moment.'
            : 'Registration verification is unavailable. Please try again later.'
        });
        return;
      }
      
      // Use email as the name field for the backend temporarily
      const submitData = {
        email: normalizedEmail,
        name: normalizedEmail, // Use email as temporary name
        displayName: normalizedEmail, // Use email as temporary display name
        firstName: '', // Empty for now, will be filled in setup
        lastName: '', // Empty for now, will be filled in setup
        inviteToken: inviteToken || undefined, // Include invite token if present
        captchaToken: captchaToken || undefined,
      };
      const response = await authApi.register(submitData);
      
      if (response.success) {
        // Check if we have a session in the response
        const session = response.data && 'session' in response.data ? response.data.session : null;
        
        if (session) {
          // Use the Zustand store to set the registration session with the invite token if present
          setRegistrationSession(submitData.email, String(session), inviteToken || undefined, returnUrl || undefined);
        } else {
          // Even without a session, we should still redirect to the confirmation page
          // Use a placeholder session value
          setRegistrationSession(submitData.email, 'pending-confirmation', inviteToken || undefined, returnUrl || undefined);
        }
        
        // Show success message
        toast.success('Please check your email for the verification code');
        
        // Navigate to confirmation page
        const confirmationParams = new URLSearchParams();
        if (isAddAccountFlow) {
          confirmationParams.set('addAccount', 'true');
        }
        const confirmationUrl = confirmationParams.toString()
          ? `/register/confirmation?${confirmationParams.toString()}`
          : '/register/confirmation';
        navigate(confirmationUrl);
        return;
      } else if (response.message) {
        // Check if this is a "user already exists" error
        if (response.message.toLowerCase().includes('already exists')) {
          setShowLoginPrompt(true);
        } else if (response.message.toLowerCase().includes('invitation only')) {
          // Handle invite-only error
          setErrors({
            general: 'Registration is currently by invitation only. Please use an invite link to register.'
          });
        } else {
          // Set general error
          setErrors({
            general: response.message
          });
        }
      }
    } catch (error) {
      logger.error('Registration error:', error);
      
      // Provide more specific error messages based on the error
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        
        if (errorMessage.includes('already exists') || errorMessage.includes('already registered')) {
          setShowLoginPrompt(true);
          setErrors({
            email: 'This email is already registered'
          });
        } else {
          setErrors({
            general: error.message
          });
        }
      } else {
        setErrors({
          general: 'An unexpected error occurred. Please try again.'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <AppLoader inline size="md" />
        <span className="text-base font-medium mt-4 text-foreground/80">Loading...</span>
      </div>
    );
  }

  // Render loading state if validating token
  if (isValidatingToken) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <AppLoader inline size="md" />
        <span className="text-base font-medium mt-4 text-foreground/80">Validating invite...</span>
      </div>
    );
  }

  return (
    <SplitAuthLayout pageType="register">
      <div className="w-full">
        {errors.general && (
          <Alert variant="destructive" className="mb-6 bg-destructive/10 border-destructive/30 text-destructive">
            <AlertDescription>{errors.general}</AlertDescription>
          </Alert>
        )}

        {showLoginPrompt && (
          <Alert variant="default" className="mb-6 border-primary/30 bg-primary/10 text-foreground">
            <AlertDescription>
              This email is already registered. Would you like to{' '}
              <Link to={`/login?email=${encodeURIComponent(normalizeEmail(formData.email))}${isAddAccountFlow ? '&addAccount=true' : ''}`} className="font-semibold underline text-primary hover:text-primary/80">
                sign in
              </Link>{' '}
              instead?
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-foreground">Email</Label>
            <div className="relative">
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                className={`h-11 pl-10 bg-background border-input ${errors.email ? 'border-destructive focus-visible:ring-destructive/20' : ''}`}
                disabled={isLoading}
                required
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                </svg>
              </div>
              {formData.email && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  animation="none"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, email: '' }));
                    if (errors.email) {
                      setErrors(prev => ({ ...prev, email: undefined }));
                    }
                  }}
                  aria-label="Clear email"
                  disabled={isLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </Button>
              )}
            </div>
            {errors.email && (
              <p className="text-xs font-medium text-destructive mt-1">
                {errors.email}
              </p>
            )}
          </div>

          {captchaSiteKey ? (
            <ReCaptcha siteKey={captchaSiteKey} onChange={setCaptchaToken} action="registration" />
          ) : requireCaptcha ? (
            <p className="text-xs text-destructive">
              Registration verification is unavailable. Please try again later.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              CAPTCHA verification is bypassed in local development.
            </p>
          )}

          <Button
            type="submit"
            className="w-full h-11 font-medium"
            disabled={!isFormValid || isLoading || (requireCaptcha && !captchaSiteKey)}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating account...
              </span>
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        {/* Divider with "or" */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-3 text-muted-foreground">or</span>
          </div>
        </div>

        {/* Google Sign-In Button */}
        <Button
          type="button"
          variant="outline"
          className="w-full h-11 bg-background border-input text-foreground hover:bg-muted"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
        >
          {isGoogleLoading ? (
            <>
              <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Connecting to Google...
            </>
          ) : (
            <>
              <GoogleIcon className="mr-2 h-4 w-4" />
              Sign up with Google
            </>
          )}
        </Button>

        {/* Sign in link */}
        <div className="mt-8 text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to={isAddAccountFlow ? '/login?addAccount=true' : '/login'} className="font-medium text-primary hover:text-primary/80 transition-colors">
              Sign in
            </Link>
          </p>

          {/* Help section - subtle and clean */}
          <div className="pt-2 border-t border-border/30">
            <p className="text-xs text-muted-foreground/80">
              Need help?{' '}
              <Link to={isAddAccountFlow ? '/register/resend-code?addAccount=true' : '/register/resend-code'} className="text-primary/80 hover:text-primary underline underline-offset-2">
                Resend verification code
              </Link>
            </p>
          </div>
        </div>
      </div>
    </SplitAuthLayout>
  );
} 
